import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import Hidden from '@material-ui/core/Hidden'
import Drawer from '@material-ui/core/Drawer'
import Menu from '@material-ui/icons/Menu'

const cyberpunkHeaderStyles = makeStyles({
  appBar: {
    display: 'flex',
    // border: "0",
    borderRadius: '3px',
    paddingTop: '25px',
    paddingBottom: '25px',
    color: '#00ffff',
    width: '100%',
    backgroundColor: 'rgba(15, 15, 25, 0.35)',
    backdropFilter: 'blur(24px) saturate(160%) brightness(1.08)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    position: 'relative',
    top: 'auto',
    zIndex: '1200',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        repeating-linear-gradient(
          90deg,
          transparent,
          transparent 2px,
          rgba(0, 255, 255, 0.02) 2px,
          rgba(0, 255, 255, 0.02) 4px
        )
      `,
      pointerEvents: 'none',
    },
  },
  absolute: {
    position: 'absolute',
    zIndex: '1200'
  },
  fixed: {
    position: 'fixed',
    zIndex: '1200'
  },
  container: {
    minHeight: '50px',
    flex: '1',
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    flexWrap: 'nowrap',
    position: 'relative',
  },
  flex: {
    flex: 1
  },
  title: {
    fontFamily: '"Orbitron", monospace',
    fontWeight: 700,
    fontSize: '1.1rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    lineHeight: '30px',
    borderRadius: '0',
    color: '#ff00ff',
    padding: '8px 16px',
    border: '1px solid rgba(255, 0, 255, 0.5)',
    background: 'rgba(255, 0, 255, 0.05)',
    margin: '0',
    textShadow: '0 0 10px rgba(255, 0, 255, 0.5)',
    transition: 'all 0.3s ease',
    '&:hover,&:focus': {
      color: '#00ffff',
      background: 'rgba(0, 255, 255, 0.1)',
      border: '1px solid rgba(0, 255, 255, 0.5)',
      textShadow: '0 0 15px rgba(0, 255, 255, 0.7)',
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
    }
  },
  appResponsive: {
    margin: '0px 10px',
    background: 'rgba(10, 10, 10, 0.98)',
    backdropFilter: 'blur(20px)',
    padding: '0px',
  },
  transparent: {
    backgroundColor: 'rgba(15, 15, 25, 0.35) !important',
    backdropFilter: 'blur(24px) saturate(160%) brightness(1.08)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
    paddingTop: '25px',
    paddingBottom: '25px',
    color: '#00ffff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  dark: {
    color: '#00ffff',
    paddingTop: '25px',
    paddingBottom: '25px',
    backgroundColor: 'rgba(15, 15, 25, 0.55) !important',
    backdropFilter: 'blur(28px) saturate(160%) brightness(1.08)',
    boxShadow: '0 4px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  white: {
    border: '0',
    paddingTop: '25px',
    paddingBottom: '25px',
    color: '#00ffff',
    backgroundColor: 'rgba(5, 5, 12, 0.95) !important',
    boxShadow: '0 0 25px rgba(255, 0, 255, 0.3)'
  },
  drawerPaper: {
    bottom: '0',
    transitionProperty: 'top, bottom, width',
    transitionDuration: '.2s, .2s, .35s',
    transitionTimingFunction: 'linear, linear, ease',
    width: '260px',
    position: 'fixed',
    display: 'block',
    top: '0',
    height: '100vh',
    right: '0',
    left: 'auto',
    visibility: 'visible',
    overflowY: 'auto',
    borderTop: 'none',
    textAlign: 'left',
    paddingRight: '0px',
    paddingLeft: '0',
    background: 'rgba(10, 10, 10, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 0, 255, 0.3)',
    boxShadow: '0 0 30px rgba(255, 0, 255, 0.3)',
  },
  // Cyberpunk menu icon
  menuIcon: {
    color: '#ff00ff',
    fontSize: '1.5rem',
    filter: 'drop-shadow(0 0 5px rgba(255, 0, 255, 0.5))',
    transition: 'all 0.3s ease',
    '&:hover': {
      color: '#00ffff',
      filter: 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.7))',
      transform: 'scale(1.1)',
    }
  }
})

export default function CyberpunkHeader (props) {
  const classes = cyberpunkHeaderStyles()
  const [mobileOpen, setMobileOpen] = React.useState(false)

  React.useEffect(() => {
    if (props.changeColorOnScroll) {
      window.addEventListener('scroll', headerColorChange)
    }
    return function cleanup () {
      if (props.changeColorOnScroll) {
        window.removeEventListener('scroll', headerColorChange)
      }
    }
  })

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const headerColorChange = () => {
    const { color, changeColorOnScroll } = props
    const windowsScrollTop = window.pageYOffset
    if (windowsScrollTop > changeColorOnScroll.height) {
      document.body
        .getElementsByTagName('header')[0]
        .classList.remove(classes[color])
      document.body
        .getElementsByTagName('header')[0]
        .classList.add(classes[changeColorOnScroll.color])
    } else {
      document.body
        .getElementsByTagName('header')[0]
        .classList.add(classes[color])
      document.body
        .getElementsByTagName('header')[0]
        .classList.remove(classes[changeColorOnScroll.color])
    }
  }

  const { color, rightLinks, leftLinks, brand, fixed, absolute } = props
  const appBarClasses = classNames({
    [classes.appBar]: true,
    [classes[color]]: color,
    [classes.absolute]: absolute,
    [classes.fixed]: fixed
  })

  const brandComponent = <Button className={classes.title}>{brand}</Button>

  return (
    <AppBar className={appBarClasses}>
      <Toolbar className={classes.container}>
        {leftLinks !== undefined ? brandComponent : null}
        <div className={classes.flex}>
          {leftLinks !== undefined ? (
            <Hidden smDown implementation="css">
              {leftLinks}
            </Hidden>
          ) : (
            brandComponent
          )}
        </div>
        <Hidden smDown implementation="css">
          {rightLinks}
        </Hidden>
        <Hidden mdUp>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            className={classes.menuIcon}
          >
            <Menu/>
          </IconButton>
        </Hidden>
      </Toolbar>
      <Hidden mdUp implementation="js">
        <Drawer
          variant="temporary"
          anchor={'right'}
          open={mobileOpen}
          classes={{
            paper: classes.drawerPaper
          }}
          onClose={handleDrawerToggle}
        >
          <div className={classes.appResponsive}>
            {leftLinks}
            {rightLinks}
          </div>
        </Drawer>
      </Hidden>
    </AppBar>
  )
}

CyberpunkHeader.defaultProp = {
  color: 'transparent'
}

CyberpunkHeader.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'info',
    'success',
    'warning',
    'danger',
    'transparent',
    'white',
    'rose',
    'dark'
  ]),
  rightLinks: PropTypes.node,
  leftLinks: PropTypes.node,
  brand: PropTypes.string,
  fixed: PropTypes.bool,
  absolute: PropTypes.bool,
  changeColorOnScroll: PropTypes.shape({
    height: PropTypes.number.isRequired,
    color: PropTypes.oneOf([
      'primary',
      'info',
      'success',
      'warning',
      'danger',
      'transparent',
      'white',
      'rose',
      'dark'
    ]).isRequired
  })
}
